import { type PropsWithChildren, useEffect, useMemo, useState } from "react";
import { useErrorBoundary } from "react-error-boundary";

import { getFeatureTypes } from "../../api/fetch.ts";
import { TrackEnum } from "../../openapi";
import { syntenyViewerDefaultTracks } from "../../settings.ts";
import { useGenomeViewerStore } from "../../store/store.ts";
import type { TrackConfiguration, TrackFeatures, TrackType } from "../../types.ts";
import { TrackConfigurationContext } from "./TrackConfigurationContext.ts";

export const TrackConfigurationProvider = ({ children }: PropsWithChildren) => {
  const getTrackConfiguration = useGenomeViewerStore((store) => store.getTrackConfiguration);
  const [trackConfiguration, setTrackConfiguration] = useState(new Map<string, TrackFeatures>(getTrackConfiguration()));
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const removeTrackGroups = useGenomeViewerStore((store) => store.removeTrackGroups);
  const reorderTrackGroups = useGenomeViewerStore((state) => state.reorderTrackGroupsById);
  const updateTrackConfiguration = useGenomeViewerStore((store) => store.applyTrackConfiguration);
  const [groupsToDelete, setGroupsToDelete] = useState<string[]>([]);
  const { showBoundary } = useErrorBoundary();

  const [availableTrackConfiguration, setAvailableTrackConfiguration] = useState(new Map<string, TrackFeatures>());

  const trackGroups = useGenomeViewerStore((store) => store.trackGroups);

  const defaultTrackTypes: TrackType[] = useMemo(() => syntenyViewerDefaultTracks.map((track) => track.type), []);

  // provider is mounted when dialog opens.
  useEffect(() => {
    const fetchedTrackConfiguration: TrackConfiguration = new Map();
    for (const [groupId, trackGroup] of trackGroups) {
      const trackFeatures: TrackFeatures = {
        defaultTypes: defaultTrackTypes,
        nuFeatureTypes: [],
      };
      // Fetch available nu_features
      getFeatureTypes(trackGroup.contigId).then((featureTypes) => {
        if (featureTypes.nuFeatureTypes.length > 0) {
          trackFeatures.defaultTypes.push(TrackEnum.NUCLEOTIDE_FEATURE);
        }
        trackFeatures.nuFeatureTypes = featureTypes.nuFeatureTypes;
        fetchedTrackConfiguration.set(groupId, trackFeatures);
      });
    }
    setAvailableTrackConfiguration(fetchedTrackConfiguration);
  }, [defaultTrackTypes, trackGroups]);

  const applyChanges = () => {
    // handle delete groups
    removeTrackGroups(groupsToDelete);

    for (const id of groupsToDelete) {
      trackConfiguration.delete(id);
    }

    // handle reordering
    reorderTrackGroups([...trackConfiguration.entries()].map(([groupId, _]) => groupId));

    updateTrackConfiguration(getTrackConfiguration(), trackConfiguration, showBoundary);

    setGroupsToDelete([]);
  };

  const value = {
    trackConfiguration,
    setTrackConfiguration,
    selectedGroupIds,
    setSelectedGroupIds,
    applyChanges,
    setGroupsToDelete,
    groupsToDelete,
    availableTrackConfiguration,
  };

  return <TrackConfigurationContext.Provider value={value}>{children}</TrackConfigurationContext.Provider>;
};
